import { useCallback, useContext, useEffect, useState } from 'react';
import { Context } from '../context/context';
import { GroupBuyRequest, IConfig, TPayment } from '../config/interfaces';
import { createGroupBuy, fetchSettings } from '../services/requests';
import { getCurrencyInfoByCountry } from '../services/currency';
import { totalQuantities } from '../utils/products';
import { getCobuyerLabel, getPrimaryBuyerLabel, hasProductTypeWithoutQuantity, isPropertyRental } from '../config/constants';
import { toast } from 'sonner';
import { buildWidgetAnalyticsContext, postParentAnalyticsEvent } from '../analytics/widgetTracking';

type PrimaryBuyerPayload = {
	first_name: any;
	last_name: any;
	country: any;
	delivery_method: string;
	city: any;
	email: any;
	phone?: any; // Optional - only included if valid
	quantity: number;
	fees: number;
	shipping_postcode?: string;
	shipping_city?: string;
	shipping_province?: string;
	shipping_address?: string;
	shipping_longitude?: number;
	shipping_latitude?: string;
};

export const usePaymentForm = () => {
	const [config, setConfig] = useState<IConfig>(JSON.parse(useContext(Context)));

	const emptyContainer: TPayment = {
		amount: '',
	};

	const [step, setStep] = useState(1);

	const [payments, setPayments] = useState<TPayment[]>([{ ...emptyContainer }, { ...emptyContainer }]); // Start with primary buyer and one co-buyer

	const [errors, setErrors] = useState<Record<number, Partial<TPayment>>>({});
	
	const [collapsedForms, setCollapsedForms] = useState<Set<number>>(new Set());

	const [groupBuySettings, setGroupBuySettings] = useState<GroupBuyRequest | undefined>();

	const [editIndex, setEditIndex] = useState<number | null>(null);

	const [loader, setLoader] = useState(true);

	const [primaryBuyerDeliveryMethod, setPrimaryBuyerDeliveryMethod] = useState('shipping');

	const [primaryBuyerShippingAddress, setPrimaryBuyerShippingAddress] = useState({
		country: '',
		city: '',
		postal_code: '',
		province: '',
		longitude: '',
		latitude: '',
		address: '',
	});

	const handleShippingAddressChange = (field: string, value: string) => {
		setPrimaryBuyerShippingAddress((prev) => ({ ...prev, [field]: value }));
	};

	const orderQuantity = totalQuantities(config.order.map((order) => ({ title: order.name, quantity: order.quantity })));

	const hideQuantity =
		hasProductTypeWithoutQuantity(config) ||
		orderQuantity.type === 'mixed-case' ||
		(orderQuantity.type === 'bottles' && orderQuantity.value === 1);

	const getSettings = useCallback(async () => {
		const [data, error] = await fetchSettings();

		if (data) {
			const response: GroupBuyRequest = data.data;

			const getCurrency = await getCurrencyInfoByCountry(response.business.country);

			const updateField = (field: keyof GroupBuyRequest['customer_details_config'], containerField: keyof TPayment) => {
				if (response.customer_details_config[field]) {
					// Update the first payment (primary buyer) with empty field
					setPayments(prev => prev.map((payment, index) => 
						index === 0 ? { ...payment, [containerField]: '' } : payment
					));

					emptyContainer[containerField] = '';
				}
			};

			updateField('fullname', 'firstName');

			updateField('fullname', 'lastName');

			updateField('email', 'email');

			updateField('phone_no', 'phone_no');

			if (!hideQuantity) updateField('quantity', 'quantity');

			setGroupBuySettings({
				...response,
				country: getCurrency,
			});
		}

		setLoader(false);
	}, []);

	useEffect(() => {
		getSettings();
	}, []);

	// Legacy compatibility - not needed with new structure but keeping for now

	const orderAmount = config.order.reduce((accumulator, currentAmount) => {
		return accumulator + Number(currentAmount.price) * Number(currentAmount.quantity);
	}, 0);

	const remainingQuantity =
		orderQuantity.value - payments.reduce((sum, payment) => sum + Number(payment.quantity || 0), 0);

	// Custom validator function
	const validateForm = () => {
		let isValid = true;
		// Reset previous errors
		setErrors({});

		// Validate first name length
		if (currentPayment.name && currentPayment.name.length < 2) {
			setErrors((prev) => ({ ...prev, name: 'First name must be at least 2 characters' }));
			isValid = false;
		}

		// Validate email format
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const hotmailRegex = /@hotmail\./i;

		if (currentPayment.email && !emailRegex.test(currentPayment.email)) {
			setErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
			isValid = false;
		} else if (hotmailRegex.test(currentPayment.email || '')) {
			setErrors((prev) => ({
				...prev,
				email: 'We do not support Hotmail emails. Please use a different email provider.',
			}));
			isValid = false;
		} else if (payments.find((payment: TPayment) => payment.email === currentPayment.email)) {
			setErrors((prev) => ({ ...prev, email: 'This is an existing email, please enter a new email' }));
			isValid = false;
		}

		if (Number(currentPayment.amount) + totalPaymentAmount > orderAmount) {
			setErrors((prev) => ({ ...prev, amount: 'Amount exceeds the remaining order amount' }));
			isValid = false;
		}

		if (Number(currentPayment.quantity) > remainingQuantity && currentPayment.quantity) {
			setErrors((prev) => ({ ...prev, quantity: 'Quantity exceeds the remaining order quantity' }));
			isValid = false;
		}

		// Check if any required field is empty - allow 0 values for amount and quantity  
		const hasEmptyRequiredFields = Object.keys(currentPayment).some((field) => {
			const value = currentPayment?.[field as keyof TPayment];
			// Allow 0 values for amount and quantity fields
			if (field === 'amount' || field === 'quantity') {
				return value === '' || value === null || value === undefined;
			}
			// For other fields, check if empty
			return !value || (typeof value === 'string' && value.trim() === '');
		});
		
		return !hasEmptyRequiredFields && isValid;
	};

	const addCoBuyer = () => {
		// Check if primary buyer (index 0) has been saved (is collapsed)
		const primaryBuyerSaved = collapsedForms.has(0);
		if (!primaryBuyerSaved) {
			// Don't allow adding guests until primary buyer is saved
			return;
		}
		setPayments((prev) => [...prev, { ...emptyContainer }]);
	};

	const removePayment = (index: number) => {
		if (index === 0) return; // Prevent removing primary buyer
		setPayments((prev) => prev.filter((_, idx) => idx !== index));
		// Clean up errors for removed payment
		setErrors((prev) => {
			const newErrors = { ...prev };
			delete newErrors[index];
			// Reindex errors for payments after removed index
			const updatedErrors: Record<number, Partial<TPayment>> = {};
			Object.keys(newErrors).forEach(key => {
				const idx = parseInt(key);
				if (idx > index) {
					updatedErrors[idx - 1] = newErrors[idx];
				} else {
					updatedErrors[idx] = newErrors[idx];
				}
			});
			return updatedErrors;
		});
		// Clean up and reindex collapsed forms
		setCollapsedForms((prev) => {
			const newSet = new Set<number>();
			prev.forEach(collapsedIndex => {
				if (collapsedIndex < index) {
					// Keep indices before the removed one
					newSet.add(collapsedIndex);
				} else if (collapsedIndex > index) {
					// Shift down indices after the removed one
					newSet.add(collapsedIndex - 1);
				}
				// Skip the removed index
			});
			return newSet;
		});
	};

	const updatePayment = (index: number, field: keyof TPayment, value: string) => {
		const newPayments = payments.map((payment, idx) => 
			idx === index ? { ...payment, [field]: value } : payment
		);
		
		setPayments(newPayments);
		
		// Clear specific error for this field
		if (errors[index]?.[field]) {
			setErrors(prev => ({
				...prev,
				[index]: {
					...prev[index],
					[field]: undefined
				}
			}));
		}
		
		// If updating quantity or amount, check if global validation errors should be cleared
		if (field === 'quantity' || field === 'amount') {
			const totalAllocatedQuantity = newPayments.reduce((sum, payment) => sum + Number(payment.quantity || 0), 0);
			const totalAllocatedAmount = newPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
			
			setErrors(prev => {
				const newErrors = { ...prev };
				let hasChanges = false;
				
				// Clear quantity validation errors if total quantity is now correct
				if (totalAllocatedQuantity === orderQuantity.value) {
					Object.keys(newErrors).forEach(key => {
						const idx = parseInt(key);
						if (newErrors[idx]?.quantity && typeof newErrors[idx].quantity === 'string' && 
							(newErrors[idx].quantity as string).includes('Total quantity must equal')) {
							newErrors[idx] = { ...newErrors[idx], quantity: undefined };
							hasChanges = true;
						}
					});
				}
				
				// Clear amount validation errors if amounts are within limits
				Object.keys(newErrors).forEach(key => {
					const idx = parseInt(key);
					if (newErrors[idx]?.amount && typeof newErrors[idx].amount === 'string') {
						const errorMsg = newErrors[idx].amount as string;
						// Clear "exceed order total" errors
						if (errorMsg.includes('exceed') && totalAllocatedAmount <= orderAmount) {
							newErrors[idx] = { ...newErrors[idx], amount: undefined };
							hasChanges = true;
						}
						// Clear "Total amount" errors if we're not in final validation
						if (errorMsg.includes('Total amount')) {
							newErrors[idx] = { ...newErrors[idx], amount: undefined };
							hasChanges = true;
						}
					}
				});
				
				// Clean up empty error objects
				Object.keys(newErrors).forEach(key => {
					const idx = parseInt(key);
					const errorObj = newErrors[idx];
					if (errorObj && Object.values(errorObj).every(val => val === undefined || val === '')) {
						delete newErrors[idx];
						hasChanges = true;
					}
				});
				
				return hasChanges ? newErrors : prev;
			});
		}
	};

	const validateSinglePayment = (payment: TPayment, index: number): Partial<TPayment> => {
		const paymentErrors: Partial<TPayment> = {};

		// Validate required fields based on settings
		if (groupBuySettings?.customer_details_config.fullname) {
			if (!payment.firstName || payment.firstName.trim().length < 2) {
				paymentErrors.firstName = 'First name must be at least 2 characters' as any;
			}
			if (!payment.lastName || payment.lastName.trim().length < 2) {
				paymentErrors.lastName = 'Last name must be at least 2 characters' as any;
			}
		}

		if (groupBuySettings?.customer_details_config.email) {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			const hotmailRegex = /@hotmail\./i;

			if (!payment.email || !emailRegex.test(payment.email)) {
				paymentErrors.email = 'Invalid email format' as any;
			} else if (hotmailRegex.test(payment.email)) {
				paymentErrors.email = 'We do not support Hotmail emails. Please use a different email provider.' as any;
			}
			
			// Check for duplicate emails
			const duplicateEmail = payments.find((p, i) => i !== index && p.email === payment.email);
			if (duplicateEmail) {
				paymentErrors.email = `This email is already used by another ${index === 0 ? getPrimaryBuyerLabel(config).toLowerCase() : getCobuyerLabel(config).toLowerCase()}` as any;
			}
		}

		// Phone is now optional - no validation required
		// if (groupBuySettings?.customer_details_config.phone_no && !payment.phone_no) {
		// 	paymentErrors.phone_no = 'Phone number is required' as any;
		// }

		// Validate amount - check if it exceeds remaining order amount
		const currentAmount = Number(payment.amount) || 0;
		const otherPaymentsTotal = payments.reduce((sum, p, i) => 
			i !== index ? sum + Number(p.amount || 0) : sum, 0
		);
		
		if (!payment.amount || payment.amount === '') {
			paymentErrors.amount = 'Amount is required' as any;
		} else if (currentAmount + otherPaymentsTotal > orderAmount) {
			paymentErrors.amount = `Amount would exceed order total of $${orderAmount}` as any;
		}

		// Validate quantity if required
		if (!hideQuantity && groupBuySettings?.customer_details_config.quantity) {
			if (!payment.quantity || payment.quantity === '') {
				paymentErrors.quantity = 'Quantity is required' as any;
			}
		}

		return paymentErrors;
	};

	const saveAndCollapseForm = (index: number) => {
		const payment = payments[index];
		const validationErrors = validateSinglePayment(payment, index);
		
		if (Object.keys(validationErrors).length > 0) {
			// Set errors and don't collapse
			setErrors(prev => ({
				...prev,
				[index]: validationErrors
			}));
			return;
		}
		
		// Clear any existing errors for this payment
		setErrors(prev => {
			const newErrors = { ...prev };
			delete newErrors[index];
			return newErrors;
		});
		
		setCollapsedForms(prev => new Set([...prev, index]));

		// Notify parent about identity if email is present
		if (payment.email) {
			window.parent.postMessage(JSON.stringify({ action: 'identity', email: payment.email }), '*');
		}
	};

	const expandForm = (index: number) => {
		setCollapsedForms(prev => {
			const newSet = new Set(prev);
			newSet.delete(index);
			return newSet;
		});
	};

	// Legacy function - keeping for compatibility
	const updateCurrentPayment = (field: keyof TPayment, value: string) => {
		// Update the first payment (primary buyer)
		updatePayment(0, field, value);
	};

	const totalPaymentAmount = payments.reduce((accumulator, currentAmount) => {
		return accumulator + Number(currentAmount.amount);
	}, 0);

	// For legacy compatibility - can be removed later
	const currentPayment = payments[0] || emptyContainer;
	
	const handleEdit = (idx: number) => {
		// With the new approach, editing is handled directly in the form
		// This function is kept for compatibility
		setEditIndex(idx);
	};

	const [hasPaid] = useState(false);

	const [nextStep] = useState(1);

	const handleNextStep = (doubleSteps?: boolean) => {
		setStep((prev) => (doubleSteps ? prev + 2 : ++prev));
	};

	const validateBeforeSubmit = () => {
		// Check if there are any co-buyers with $0 contribution when trying to proceed
		const hasZeroContributors = payments.some(payment => Number(payment.amount) === 0);
		
		if (hasZeroContributors && totalPaymentAmount < orderAmount) {
			return { isValid: false, message: 'Co-buyers with $0 contribution are only allowed when the total amount equals the order amount.' };
		}
		
		return { isValid: true, message: '' };
	};

	const validateAllPayments = () => {
		let hasErrors = false;
		const newErrors: Record<number, Partial<TPayment>> = {};

		payments.forEach((payment, index) => {
			const paymentErrors: Partial<TPayment> = {};

			// Validate required fields based on settings
			if (groupBuySettings?.customer_details_config.fullname) {
				if (!payment.firstName || payment.firstName.trim().length < 2) {
					paymentErrors.firstName = 'First name must be at least 2 characters' as any;
					hasErrors = true;
				}
				if (!payment.lastName || payment.lastName.trim().length < 2) {
					paymentErrors.lastName = 'Last name must be at least 2 characters' as any;
					hasErrors = true;
				}
			}

			if (groupBuySettings?.customer_details_config.email) {
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
				const hotmailRegex = /@hotmail\./i;

				if (!payment.email || !emailRegex.test(payment.email)) {
					paymentErrors.email = 'Invalid email format' as any;
					hasErrors = true;
				} else if (hotmailRegex.test(payment.email)) {
					paymentErrors.email = 'We do not support Hotmail emails. Please use a different email provider.' as any;
					hasErrors = true;
				}
				
				// Check for duplicate emails
				const duplicateEmail = payments.find((p, i) => i !== index && p.email === payment.email);
				if (duplicateEmail) {
					paymentErrors.email = `This email is already used by another ${index === 0 ? getPrimaryBuyerLabel(config).toLowerCase() : getCobuyerLabel(config).toLowerCase()}` as any;
					hasErrors = true;
				}
			}

			// Phone is now optional - no validation required
			// if (groupBuySettings?.customer_details_config.phone_no && !payment.phone_no) {
			// 	paymentErrors.phone_no = 'Phone number is required' as any;
			// 	hasErrors = true;
			// }

			// Validate amount
			if (!payment.amount || payment.amount === '') {
				paymentErrors.amount = 'Amount is required' as any;
				hasErrors = true;
			}

			// Validate quantity if required
			if (!hideQuantity && groupBuySettings?.customer_details_config.quantity) {
				if (!payment.quantity || payment.quantity === '') {
					paymentErrors.quantity = 'Quantity is required' as any;
					hasErrors = true;
				}
			}

			if (Object.keys(paymentErrors).length > 0) {
				newErrors[index] = paymentErrors;
			}
		});

		setErrors(newErrors);
		return !hasErrors;
	};

	const handleContinue = () => {
		if (step === 1) {
			// Validate all payments
			const formIsValid = validateAllPayments();
			if (!formIsValid) {
				// Show toast for validation errors
				const errorMessages = Object.values(errors).flatMap(errorObj => 
					Object.values(errorObj).filter(msg => msg)
				);
				if (errorMessages.length > 0) {
					toast.error('Please fix the form errors before continuing');
				} else {
					toast.error('Please fill in all required fields');
				}
				return;
			}

			// Validate zero amount contributors before proceeding
			const submitValidation = validateBeforeSubmit();
			if (!submitValidation.isValid) {
				setErrors((prev) => ({ 
					...prev, 
					0: { ...prev[0], amount: submitValidation.message as any }
				}));
				toast.error(submitValidation.message);
				return;
			}

			// Check if all quantities are allocated (someone must receive the items)
			const totalAllocatedQuantity = payments.reduce((sum, payment) => sum + Number(payment.quantity || 0), 0);
			
			if (!hideQuantity && totalAllocatedQuantity !== orderQuantity.value) {
				const errorMessage = `Total quantity must equal ${orderQuantity.value}. Currently allocated: ${totalAllocatedQuantity}`;
				setErrors((prev) => ({ 
					...prev, 
					0: { ...prev[0], quantity: errorMessage as any }
				}));
				toast.error(errorMessage);
				return;
			}

			// Check if total amount equals order amount
			if (totalPaymentAmount === orderAmount) {
				
				const primaryBuyerHasQuantity = payments[0] && Number(payments[0]?.quantity || 0) > 0;
				if (primaryBuyerHasQuantity || hideQuantity) {
					if (isPropertyRental(config)) {
						handleCreateGroupBuy(payments);
					} else {
						handleNextStep();
					}
				} else {
					handleCreateGroupBuy(payments);
				}
			} else if (totalPaymentAmount < orderAmount) {
				// Need more buyers or amounts
				const errorMessage = `Total amount ($${totalPaymentAmount}) is less than order amount ($${orderAmount}). Add more ${getCobuyerLabel(config, 0).toLowerCase()}s or increase amounts.`;
				setErrors((prev) => ({ 
					...prev, 
					0: { ...prev[0], amount: errorMessage as any }
				}));
				toast.error(errorMessage);
			} else {
				// Total exceeds order amount
				const errorMessage = `Total amount ($${totalPaymentAmount}) exceeds order amount ($${orderAmount}).`;
				setErrors((prev) => ({ 
					...prev, 
					0: { ...prev[0], amount: errorMessage as any }
				}));
				toast.error(errorMessage);
			}
		}

		if (step === 2) {
			return handleCreateGroupBuy(payments);
		}

		if (step > 1) {
			handleNextStep();
		}
	};

	const [groupBuyResponse, setGroupBuyResponse] = useState<
		| {
				transaction: { groupbuy_transaction_id: string; transaction_id: string; subTotal: number };
				clientSecret: string;
		  }
		| undefined
	>();

	const [responseError, setResponseError] = useState('');

	// Helper function to check if phone is valid (not just country code)
	const isValidPhone = (phone: string | undefined): boolean => {
		if (!phone || !phone.trim()) return false;
		// Remove spaces and check if it's more than just country code (typically 1-4 digits)
		const cleaned = phone.replace(/\s/g, '');
		// If it's just a country code (like "+886" or "+1"), it's not valid
		// A valid phone should have at least 7 digits after the country code
		const digitsAfterPlus = cleaned.replace(/^\+/, '').replace(/\D/g, '');
		return digitsAfterPlus.length > 4; // More than just country code
	};

	const handleCreateGroupBuy = async (payments: any[]) => {
		setLoader(true);
		// Update this payments array
		const primaryBuyer = payments[0];

		const amount =
			Number(totalPaymentAmount) !== orderAmount
				? Number(totalPaymentAmount) + Number(currentPayment.amount)
				: Number(totalPaymentAmount);

		const basePrimaryBuyer: PrimaryBuyerPayload = {
			first_name: primaryBuyer.firstName,
			last_name: primaryBuyer.lastName,
			country: primaryBuyer.country,
			delivery_method: isPropertyRental(config) ? 'pickup' : primaryBuyerDeliveryMethod,
			city: primaryBuyer.city,
			email: primaryBuyer.email,
			...(isValidPhone(primaryBuyer.phone_no) && { phone: primaryBuyer.phone_no }),
			quantity: Number(primaryBuyer?.quantity) || 1,
			fees: Number(primaryBuyer.amount),
		};

		const payload = {
			item_summary: config.order.map((order) => ({
				name: order.name,
				quantity: order.quantity,
				description: order.description || order.name,
				price: order.price,
				sub_total: order.price * order.quantity,
				product_type: order.productType,
				image: order.image || '',
				product_number: order.id,
			})),
			primary_buyer: basePrimaryBuyer,
			cobuyers: payments
				.filter((_, idx) => idx > 0)
				.map((payment) => {
					const cobuyer: any = {
						first_name: payment.firstName,
						last_name: payment.lastName,
						email: payment.email,
						quantity: Number(payment?.quantity) || 1,
						address: payment.address,
						country: primaryBuyer.country,
						postal_code: primaryBuyer.postal_code?.toLowerCase(),
						city: primaryBuyer.city,
						fees: Number(payment.amount),
					};
					// Only include phone if it's valid
					if (isValidPhone(payment.phone_no)) {
						cobuyer.phone = payment.phone_no;
					}
					return cobuyer;
				}),
			groupbuy: {
				total: amount,
				source: config.source,
			},
		};

		if (primaryBuyerDeliveryMethod === 'shipping' && !isPropertyRental(config)) {
			const latitude = primaryBuyerShippingAddress.latitude ? Number(primaryBuyerShippingAddress.latitude) : undefined;
			const longitude = primaryBuyerShippingAddress.longitude ? Number(primaryBuyerShippingAddress.longitude) : undefined;
			payload.primary_buyer = {
				...payload.primary_buyer,
				shipping_address: primaryBuyerShippingAddress.address,
				shipping_latitude: latitude,
				shipping_longitude: longitude,
				shipping_postcode: primaryBuyerShippingAddress.postal_code || '',
				shipping_city: primaryBuyerShippingAddress.city,
				shipping_province: primaryBuyerShippingAddress.province,
			};
		}

		const [data, error] = await createGroupBuy(payload as any);

		if (data) {
			const allocatedQuantity = payments.reduce((sum, payment) => sum + Number(payment.quantity || 0), 0);

			setGroupBuyResponse(data.data);
			setStep(3);
			setResponseError('');
			postParentAnalyticsEvent('cobuyr_groupbuy_created', {
				...buildWidgetAnalyticsContext(config),
				participant_count: payments.length,
				cobuyer_count: Math.max(payments.length - 1, 0),
				group_total: Number(amount.toFixed(2)),
				allocated_quantity: allocatedQuantity,
				delivery_method: primaryBuyerDeliveryMethod,
			});
			return setLoader(false);
		} else {
			// setEditIndex(payments.length - 1);
			// setCurrentPayment(payments[payments.length - 1]);
			const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred. Please try again.';
			setResponseError(errorMessage);
			// Show error toast
			toast.error(errorMessage);
		}

		setLoader(false);
	};

	const handleTitle = () => {
		// Always show "Continue" for the main action button
		return 'Continue';
	};

	const handleEditConfig = (field: keyof typeof config, data: any) => {
		setConfig((prev) => ({ ...prev, [field]: data }));
	};

	const disableContinue = (() => {
		if (!groupBuySettings) return true;
		
		const config = groupBuySettings.customer_details_config;
		
		// Check if primary buyer (first payment) has required fields filled
		const primaryBuyer = payments[0];
		if (!primaryBuyer) return true;
		
		// First name, Last name, and Email are now always mandatory (if config requires them)
		if (config.fullname && (!primaryBuyer.firstName || !primaryBuyer.lastName)) {
			return true;
		}
		if (config.email && !primaryBuyer.email) {
			return true;
		}
		// Phone is optional - no longer required
		// if (config.phone_no && !primaryBuyer.phone_no) {
		// 	return true;
		// }
		if (!primaryBuyer.amount && primaryBuyer.amount !== '0') {
			return true;
		}
		if (!hideQuantity && config.quantity && !primaryBuyer.quantity && primaryBuyer.quantity !== '0') {
			return true;
		}
		
		// Also disable if there are any validation errors
		const hasValidationErrors = Object.keys(errors).length > 0;
		
		return hasValidationErrors;
	})();

	return {
		handleContinue,
		handleEditConfig,
		handleShippingAddressChange,
		setPrimaryBuyerDeliveryMethod,
		handleEdit,
		handleTitle,
		updateCurrentPayment,
		updatePayment,
		addCoBuyer,
		saveAndCollapseForm,
		expandForm,
		collapsedForms,
		handleCreateGroupBuy,
		validateForm,
		validateAllPayments,
		validateBeforeSubmit,
		removePayment,
		groupBuyResponse,
		hasPaid,
		groupBuySettings,
		currentPayment,
		responseError,
		errors,
		loader,
		orderAmount,
		disableContinue,
		remainingQuantity,
		config,
		hideQuantity,
		payments,
		totalPaymentAmount,
		setPayments,
		setStep,
		nextStep,
		step,
	};
};
